import { Locales } from '@griply/types';
import { Frequency, RRule } from 'rrule';
import { IconFormNames } from 'shared/assets/icons';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { Goal, NewGoalWithID } from 'shared/types/goal';
import {
  CompletionTargetUnit,
  FrequencyUnit,
  RruleReferenceDate,
} from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { NewTagWithID, Tag } from 'shared/types/tag';
import { CreateTask } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { isNumber } from 'shared/utils/is-number';
import { WordId } from 'shared/utils/nlp/get-ids-for-words';
import { processTaskPropertiesFromString } from 'shared/utils/nlp/process-task-properties-from-string';
import { removeNlpPropertiesFromString } from 'shared/utils/nlp/remove-nlp-properties-from-string';
import { v4 as uuidv4 } from 'uuid';

export type BuildCreateTaskFromPropertiesOptions = {
  initialProperties: Partial<CreateTask>;
  changedProps: (keyof CreateTask)[];
  nlpString: string;
  nlpRegex: RegExp;
  wordIds: WordId[];
  ignoredWordIds: ID[];
  language: Locales;
  dateFormat: DateFormatOptions;
  createHabitAllowed: boolean;
  scheduleTargetAllowed: boolean;
  dateAnchored?: boolean;
  description: string;
  iconName: IconFormNames;
  hideOnTodo: boolean;
  hideOnHabitList: boolean;
  completionsPerDay: number;
  scheduleEndDate: Timestamp | null;
  goals: (Goal | NewGoalWithID)[];
  lifeAreas: LifeArea[];
  tags: (Tag | NewTagWithID)[];
  today: Timestamp;
};

export const buildCreateTaskFromProperties = ({
  initialProperties,
  changedProps,
  nlpString,
  nlpRegex,
  wordIds,
  ignoredWordIds,
  language,
  dateFormat,
  createHabitAllowed,
  scheduleTargetAllowed,
  dateAnchored,
  description,
  iconName,
  hideOnTodo,
  hideOnHabitList,
  completionsPerDay,
  scheduleEndDate,
  goals,
  lifeAreas,
  tags,
  today,
}: BuildCreateTaskFromPropertiesOptions): CreateTask => {
  // filter the initialProperties to only include those that are not changed
  const filteredInitialProperties = Object.entries(initialProperties).reduce<
    Partial<CreateTask>
  >((acc, [key, value]) => {
    if (changedProps.includes(key as keyof CreateTask)) {
      return acc;
    }
    return {
      ...acc,
      [key]: value,
    };
  }, {});

  // get the base of the task
  const result: CreateTask = {
    ...filteredInitialProperties,
    // process the words into a name without the connected words
    name: removeNlpPropertiesFromString(nlpString, {
      nlpRegex,
      wordIds,
      ignoredWordIds,
      language,
      dateFormat,
      createHabitAllowed,
      scheduleTargetAllowed,
    }).trim(),
    description,
    // Parens are load-bearing: `??` binds tighter than `?:`, so an explicit type would be discarded.
    type:
      filteredInitialProperties.type ??
      (filteredInitialProperties?.schedules?.length
        ? createHabitAllowed
          ? TaskType.Habit
          : TaskType.Repeating
        : TaskType.Task),
    goalId: filteredInitialProperties?.goalId ?? null,
    lifeAreaId: filteredInitialProperties?.lifeAreaId ?? null,
    sectionId: filteredInitialProperties?.sectionId ?? null,
    tagIds: filteredInitialProperties.tagIds ?? null,
    reminderTime: filteredInitialProperties?.reminderTime ?? null,
    // A cleared list leaves no token in the input; `null` would re-arm the settings default.
    reminders:
      filteredInitialProperties?.reminders ??
      (changedProps.includes('reminders') ? [] : null),
    parentIds: filteredInitialProperties?.parentIds ?? null,
    priority: filteredInitialProperties?.priority ?? null,
    schedules: filteredInitialProperties?.schedules ?? null,
    iconName,
  };

  const properties = processTaskPropertiesFromString(nlpString, {
    language,
    dateFormat,
    ignoredWordIds,
    wordIds,
    goals,
    lifeAreas,
    tags,
    createHabitAllowed,
    scheduleTargetAllowed,
    people: [],
    dateAnchored,
  });

  Object.keys(properties).forEach((key) => {
    const _key = key as keyof typeof properties;

    if (_key === 'goal') {
      result.goalId = properties[_key] ?? null;
    }

    if (_key === 'lifeArea') {
      result.lifeAreaId = properties[_key] ?? null;
    }

    if (_key === 'tags') {
      result.tagIds = properties[_key] ?? null;
    }

    if (_key === 'deadline') {
      result.deadlineDeadline = properties[_key] ?? null;
    }

    if (_key === 'priority') {
      result.priority = properties[_key] ?? null;
    }

    if (_key === 'reminders') {
      const parsedReminders = properties[_key];
      result.reminders = parsedReminders?.length
        ? parsedReminders.map((reminder) => ({ ...reminder, id: uuidv4() }))
        : null;
    }

    // habit / repeating
    if (properties.target || properties.rrule) {
      result.hideOnTodo = hideOnTodo;
      result.type =
        createHabitAllowed && !hideOnHabitList
          ? TaskType.Habit
          : TaskType.Repeating;
      result.endStrategy = null;
      result.deadlineDeadline = null;

      if (_key === 'target') {
        const timeslot =
          isNumber(properties.startTime) || isNumber(properties.duration)
            ? {
                startTime: properties.startTime ?? null,
                duration: properties.duration ?? null,
              }
            : filteredInitialProperties.timeslot ?? null;
        const frequency = properties.target?.unit!;

        result.schedules = [
          {
            entries: [],
            timeslot,
            startDate: properties.dueDate ?? today,
            endDate: scheduleEndDate,
            frequency: {
              count: properties.target?.count!,
              unit: properties.target?.unit!,
              label: properties.target?.label ?? null,
            },
            rrule: {
              format: new RRule({
                freq:
                  frequency === FrequencyUnit.Month
                    ? Frequency.MONTHLY
                    : Frequency.DAILY,
              }).toString(),
              referenceDate: RruleReferenceDate.StartDate,
            },
            completionTarget: {
              unit: CompletionTargetUnit.Day,
              count: 1,
            },
          },
        ];
      }

      if (_key === 'rrule') {
        const timeslot =
          isNumber(properties.startTime) || isNumber(properties.duration)
            ? {
                startTime: properties.startTime ?? null,
                duration: properties.duration ?? null,
              }
            : filteredInitialProperties.timeslot ?? null;

        result.schedules = [
          {
            entries: [],
            timeslot,
            startDate: properties.dueDate ?? today,
            endDate: scheduleEndDate,
            frequency: null,
            rrule: {
              format: properties.rrule!,
              referenceDate: RruleReferenceDate.StartDate,
            },
            completionTarget: {
              unit: CompletionTargetUnit.Day,
              count: completionsPerDay,
            },
          },
        ];
      }
    }

    // regular task
    if (!(properties.target || properties.rrule)) {
      result.type = TaskType.Task;
      result.schedules = null;

      if (_key === 'dueDate') {
        result.endStrategy = {
          deadline: properties[_key],
          completionCount: null,
        };
      }

      if (_key === 'startTime' || _key === 'duration') {
        // Preserve the field NLP didn't touch — e.g. typing "1h30m" should
        // update only `duration` and keep the calendar-anchored `startTime`.
        const startTimeFromNlp = 'startTime' in properties;
        const durationFromNlp = 'duration' in properties;
        result.timeslot = {
          startTime: startTimeFromNlp
            ? properties.startTime ?? null
            : filteredInitialProperties.timeslot?.startTime ?? null,
          duration: durationFromNlp
            ? properties.duration ?? null
            : filteredInitialProperties.timeslot?.duration ?? null,
        };
      }
    }
  });

  return result;
};
